import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import { ColumnButton } from 'lib/components/ColumnButton';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import { Alert } from 'lib/components/Alert/Alert';
import { Permissions } from 'lib/permissions/roles';
import { ProductRevenueByMonthChartData } from 'lib/types/reports/ProductsTable';
import { useReportParams } from '../context/ReportParamsContext';
import { useReportsLoading } from '../context/LoadingContext';
import {
  fetchNoticesWithParams,
  fetchOrdersWithParams,
  fetchProductRevenueByMonthChartData
} from '../utils/elastic';
import { exportSearchableRecordsToCSV } from '../utils/exportOrdersToCSV';
import {
  NEWSPAPER_ORDER_CSV_COLUMNS,
  REPORT_V2_NOTICE_CSV_COLUMNS
} from '../constants/csvColumns';
import { ORDER_TYPE_COLORS } from '../constants/colors';

export default function ProductRevenueBarChart() {
  const { getOrgIdsWithPermission, getOrgIds, getDateRange } =
    useReportParams();
  const { dateRange, startDate, endDate } = getDateRange();
  const { withLoading } = useReportsLoading();
  const orgIds = getOrgIds();
  const allowedOrgIds = getOrgIdsWithPermission(Permissions.PAYMENTS_REPORT);

  const { value: chartData, error } =
    useAsyncEffect<ProductRevenueByMonthChartData | null>({
      fetchData: () =>
        withLoading(
          fetchProductRevenueByMonthChartData(allowedOrgIds, getDateRange)
        ),
      initialData: null,
      dependencies: [orgIds.sort().join(','), dateRange, startDate, endDate],
      errorConfig: {
        service: ColumnService.ELASTIC,
        message:
          '[COLUMN REPORTS] Failed to fetch revenue aggregations for Products tab',
        tags: {
          dateRange,
          startDate: startDate || '',
          endDate: endDate || ''
        }
      }
    });

  const { labels, datasets } = chartData || {
    labels: [],
    datasets: []
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom' as const },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label(context: any) {
            return `${context.dataset.label}: $${Number(
              context.parsed.y
            ).toLocaleString()}`;
          },
          footer(tooltipItems: any[]) {
            const sum = tooltipItems.reduce(
              (acc, item) => acc + Number(item.parsed.y),
              0
            );
            return `Total: $${sum.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          callback(value: string | number) {
            return `$${Number(value).toLocaleString()}`;
          }
        }
      }
    }
  };

  const handleExportCSV = async () => {
    const newspaperOrders = await fetchOrdersWithParams(
      allowedOrgIds,
      getDateRange,
      withLoading
    );

    const notices = await fetchNoticesWithParams(
      allowedOrgIds,
      getDateRange,
      withLoading
    );

    exportSearchableRecordsToCSV({
      dataAndColumns: [
        {
          data: newspaperOrders,
          columns: NEWSPAPER_ORDER_CSV_COLUMNS
        },
        {
          data: notices,
          columns: REPORT_V2_NOTICE_CSV_COLUMNS
        }
      ],
      filename: `mixed_records_export_${moment().format('YYYY-MM-DD')}.csv`
    });
  };

  if (error) {
    return (
      <Alert status="error" id="products-tab-error">
        Error: {error.message}
      </Alert>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-end mb-4">
        <ColumnButton
          id="export-csv-button"
          buttonText="Export CSV"
          onClick={handleExportCSV}
          primary
          type="button"
        />
      </div>
      <Bar
        data={{
          labels,
          datasets: datasets?.map(dataset => ({
            ...dataset,
            backgroundColor: ORDER_TYPE_COLORS[dataset.product]
          }))
        }}
        options={options}
      />
    </div>
  );
}
